import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Modal, Button, Form } from "react-bootstrap";
import { Icon } from "@iconify/react";
import toast from "react-hot-toast";
import moment from "moment";
import {
  listPhonebooks,
  createPhonebook,
  deletePhonebook,
  listPhonebookContacts,
  addPhonebookContact,
  updateContact,
  deleteContacts,
  importPhonebookContactsCsv,
  getWhatsAppErrorMessage,
  isWhatsAppApiSuccess,
  unwrapWhatsAppList,
} from "../../services/whatsappApi";
import {
  formatWhatsAppMobile,
  validateWhatsAppMobileForSubmit,
} from "../../utils/whatsappFormat";
import {
  sanitizeMobileInput,
  mobileInputFieldProps,
} from "../../utils/mobileInput";
import { downloadBlob } from "../../utils/whatsappCampaign";
import { useButtonTableStyle } from "../../utils/useButtonTableStyle";
import "./whatsapp.css";
import "./whatsapp-phonebook.css";
import WhatsAppPageShell from "./WhatsAppPageShell";

const SEARCH_DEBOUNCE_MS = 300;
const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];

const EMPTY_CONTACT_FORM = {
  name: "",
  mobile: "",
  var1: "",
  var2: "",
  var3: "",
  var4: "",
  var5: "",
};

const SAMPLE_CSV = `name,mobile,var1,var2,var3,var4,var5
John Doe,9876543210,sample1,sample2,,,`;

function reportWhatsAppError(err, fallback) {
  const msg = getWhatsAppErrorMessage(err, fallback);
  toast.error(msg);
  return msg;
}

function formatCreatedAgo(value) {
  if (!value) return "Recently";
  const m = moment(value);
  return m.isValid() ? m.fromNow() : "Recently";
}

function formatContactDate(value) {
  if (!value) return "—";
  const m = moment(value);
  return m.isValid() ? m.format("DD MMM YYYY") : "—";
}

function escapeCsvCell(value) {
  const str = String(value ?? "");
  if (/[",\n]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function downloadSampleCsv() {
  const blob = new Blob([SAMPLE_CSV], { type: "text/csv;charset=utf-8" });
  downloadBlob(blob, "phonebook-contacts-sample.csv");
}

function exportContactsCsv(contacts, phonebookName) {
  const header = "name,mobile,var1,var2,var3,var4,var5\n";
  const lines = contacts
    .map((c) =>
      [
        c.name,
        c.mobile,
        c.var1,
        c.var2,
        c.var3,
        c.var4,
        c.var5,
      ]
        .map(escapeCsvCell)
        .join(","),
    )
    .join("\n");
  const blob = new Blob([header + lines], { type: "text/csv;charset=utf-8" });
  const safeName = (phonebookName || "phonebook").replace(/[^\w-]+/g, "_");
  downloadBlob(blob, `${safeName}-contacts.csv`);
}

export default function WhatsAppPhonebooks() {
  const { id: routePhonebookId } = useParams();
  const navigate = useNavigate();
  const { buttonClass, cardClass } = useButtonTableStyle();
  const searchDebounceRef = useRef(null);
  const csvInputRef = useRef(null);

  const [phonebooks, setPhonebooks] = useState([]);
  const [phonebooksLoading, setPhonebooksLoading] = useState(true);
  const [phonebooksError, setPhonebooksError] = useState("");
  const [newPhonebookName, setNewPhonebookName] = useState("");
  const [creatingPhonebook, setCreatingPhonebook] = useState(false);

  const [contacts, setContacts] = useState([]);
  const [contactsTotal, setContactsTotal] = useState(0);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsError, setContactsError] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [contactPage, setContactPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  const [deletePhonebookTarget, setDeletePhonebookTarget] = useState(null);
  const [deletingPhonebook, setDeletingPhonebook] = useState(false);

  const [addContactModal, setAddContactModal] = useState(null);
  const [contactModalTab, setContactModalTab] = useState("manual");
  const [contactForm, setContactForm] = useState(EMPTY_CONTACT_FORM);
  const [editingContact, setEditingContact] = useState(null);
  const [savingContact, setSavingContact] = useState(false);
  const [contactFormError, setContactFormError] = useState("");

  const [importFile, setImportFile] = useState(null);
  const [importDragging, setImportDragging] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [exporting, setExporting] = useState(false);

  const [showDeleteContactsConfirm, setShowDeleteContactsConfirm] =
    useState(false);
  const [deletingContacts, setDeletingContacts] = useState(false);

  const selectedPhonebookId = routePhonebookId
    ? String(routePhonebookId)
    : null;

  const selectedPhonebook = useMemo(
    () =>
      phonebooks.find((pb) => String(pb.id) === selectedPhonebookId) || null,
    [phonebooks, selectedPhonebookId],
  );

  const totalPages = Math.max(1, Math.ceil(contactsTotal / rowsPerPage));

  const fetchPhonebooks = useCallback(async () => {
    setPhonebooksLoading(true);
    setPhonebooksError("");
    try {
      const res = await listPhonebooks();
      setPhonebooks(unwrapWhatsAppList(res));
    } catch (err) {
      const msg = reportWhatsAppError(err, "Failed to load phonebooks");
      setPhonebooksError(msg);
      setPhonebooks([]);
    } finally {
      setPhonebooksLoading(false);
    }
  }, []);

  const fetchContacts = useCallback(async () => {
    if (!selectedPhonebookId) return;
    setContactsLoading(true);
    setContactsError("");
    try {
      const offset = (contactPage - 1) * rowsPerPage;
      const res = await listPhonebookContacts(selectedPhonebookId, {
        search: searchQuery,
        limit: rowsPerPage,
        offset,
      });
      const rows = unwrapWhatsAppList(res);
      setContacts(rows);
      setContactsTotal(Number(res?.total) ?? rows.length);
      setSelectedIds([]);
    } catch (err) {
      const msg = reportWhatsAppError(err, "Failed to load contacts");
      setContactsError(msg);
      setContacts([]);
      setContactsTotal(0);
    } finally {
      setContactsLoading(false);
    }
  }, [selectedPhonebookId, searchQuery, contactPage, rowsPerPage]);

  useEffect(() => {
    fetchPhonebooks();
  }, [fetchPhonebooks]);

  useEffect(() => {
    if (phonebooksLoading || phonebooks.length === 0) return;
    if (!routePhonebookId) {
      navigate(`/whatsapp/phonebooks/${encodeURIComponent(phonebooks[0].id)}`, {
        replace: true,
      });
    }
  }, [phonebooks, phonebooksLoading, routePhonebookId, navigate]);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearchQuery(searchInput.trim());
      setContactPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchInput]);

  useEffect(() => {
    setContactPage(1);
  }, [selectedPhonebookId, rowsPerPage]);

  useEffect(() => {
    if (!selectedPhonebookId) {
      setContacts([]);
      setContactsTotal(0);
      return;
    }
    fetchContacts();
  }, [selectedPhonebookId, searchQuery, contactPage, rowsPerPage, fetchContacts]);

  const handleSelectPhonebook = (id) => {
    navigate(`/whatsapp/phonebooks/${encodeURIComponent(id)}`);
  };

  const handleCreatePhonebook = async (event) => {
    event.preventDefault();
    const name = newPhonebookName.trim();
    if (!name) {
      toast.error("Phonebook name is required");
      return;
    }
    setCreatingPhonebook(true);
    try {
      const res = await createPhonebook(name);
      if (res?.success === false) {
        throw new Error(res.message || "Failed to create phonebook");
      }
      toast.success("Phonebook created");
      setNewPhonebookName("");
      await fetchPhonebooks();
      const created =
        res?.data?.id != null
          ? res.data
          : unwrapWhatsAppList(res).find((pb) => pb.name === name);
      if (created?.id != null) {
        navigate(`/whatsapp/phonebooks/${encodeURIComponent(created.id)}`);
      }
    } catch (err) {
      reportWhatsAppError(err, "Failed to create phonebook");
    } finally {
      setCreatingPhonebook(false);
    }
  };

  const handleConfirmDeletePhonebook = async () => {
    if (!deletePhonebookTarget) return;
    setDeletingPhonebook(true);
    try {
      const res = await deletePhonebook(deletePhonebookTarget.id);
      if (res?.success === false) {
        throw new Error(res.message || "Failed to delete phonebook");
      }
      toast.success("Phonebook deleted");
      if (String(deletePhonebookTarget.id) === selectedPhonebookId) {
        navigate("/whatsapp/phonebooks");
      }
      setDeletePhonebookTarget(null);
      await fetchPhonebooks();
    } catch (err) {
      reportWhatsAppError(err, "Failed to delete phonebook");
    } finally {
      setDeletingPhonebook(false);
    }
  };

  const openAddContactModal = (pb, tab = "manual") => {
    if (String(pb.id) !== selectedPhonebookId) {
      navigate(`/whatsapp/phonebooks/${encodeURIComponent(pb.id)}`);
    }
    setAddContactModal({ phonebookId: pb.id, phonebookName: pb.name });
    setContactModalTab(tab);
    setContactForm(EMPTY_CONTACT_FORM);
    setContactFormError("");
    setImportFile(null);
    setImportResult(null);
    setEditingContact(null);
  };

  const closeAddContactModal = () => {
    setAddContactModal(null);
    setContactForm(EMPTY_CONTACT_FORM);
    setContactFormError("");
    setImportFile(null);
    setImportResult(null);
    setImportDragging(false);
  };

  const openEditContact = (contact) => {
    const displayMobile =
      String(contact.mobile || "").length === 12 &&
      String(contact.mobile).startsWith("91")
        ? contact.mobile.slice(2)
        : contact.mobile;
    setEditingContact(contact);
    setContactForm({
      name: contact.name || "",
      mobile: displayMobile || "",
      var1: contact.var1 || "",
      var2: contact.var2 || "",
      var3: contact.var3 || "",
      var4: contact.var4 || "",
      var5: contact.var5 || "",
    });
    setContactFormError("");
  };

  const closeEditContact = () => {
    setEditingContact(null);
    setContactForm(EMPTY_CONTACT_FORM);
    setContactFormError("");
  };

  const handleSaveContact = async (event) => {
    event.preventDefault();
    const name = contactForm.name.trim();
    if (!name) {
      setContactFormError("Name is required");
      return;
    }
    const mobileCheck = validateWhatsAppMobileForSubmit(contactForm.mobile);
    if (!mobileCheck.ok) {
      setContactFormError(mobileCheck.error);
      return;
    }

    const payload = {
      name,
      mobile: mobileCheck.mobile,
      var1: contactForm.var1.trim() || undefined,
      var2: contactForm.var2.trim() || undefined,
      var3: contactForm.var3.trim() || undefined,
      var4: contactForm.var4.trim() || undefined,
      var5: contactForm.var5.trim() || undefined,
    };

    const phonebookId = editingContact
      ? selectedPhonebookId
      : addContactModal?.phonebookId ?? selectedPhonebookId;

    setSavingContact(true);
    setContactFormError("");
    try {
      let res;
      if (editingContact) {
        res = await updateContact(editingContact.id, payload);
      } else {
        res = await addPhonebookContact(phonebookId, payload);
      }
      if (res?.success === false) {
        throw new Error(res.message || "Failed to save contact");
      }
      toast.success(editingContact ? "Contact updated" : "Contact saved");
      if (editingContact) closeEditContact();
      else closeAddContactModal();
      await fetchContacts();
      await fetchPhonebooks();
    } catch (err) {
      const msg = reportWhatsAppError(err, "Failed to save contact");
      setContactFormError(msg);
    } finally {
      setSavingContact(false);
    }
  };

  const toggleSelectAll = (checked) => {
    setSelectedIds(checked ? contacts.map((c) => c.id) : []);
  };

  const toggleSelectContact = (id, checked) => {
    setSelectedIds((prev) =>
      checked ? [...prev, id] : prev.filter((x) => x !== id),
    );
  };

  const handleDeleteSelectedContacts = async () => {
    if (!selectedIds.length) return;
    setDeletingContacts(true);
    try {
      const res = await deleteContacts(selectedIds);
      if (res?.success === false) {
        throw new Error(res.message || "Failed to delete contacts");
      }
      toast.success(
        `${selectedIds.length} contact${selectedIds.length > 1 ? "s" : ""} deleted`,
      );
      setShowDeleteContactsConfirm(false);
      setSelectedIds([]);
      await fetchContacts();
      await fetchPhonebooks();
    } catch (err) {
      reportWhatsAppError(err, "Failed to delete contacts");
    } finally {
      setDeletingContacts(false);
    }
  };

  const handleCsvFile = (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      toast.error("Please upload a CSV file");
      return;
    }
    setImportFile(file);
    setImportResult(null);
  };

  const handleImportCsv = async () => {
    const phonebookId = addContactModal?.phonebookId ?? selectedPhonebookId;
    if (!importFile || !phonebookId) return;
    setImporting(true);
    try {
      const res = await importPhonebookContactsCsv(phonebookId, importFile);
      if (!isWhatsAppApiSuccess(res) && res?.success === false) {
        throw new Error(res.message || "Import failed");
      }
      setImportResult(res?.data || res);
      toast.success("CSV import completed");
      await fetchContacts();
      await fetchPhonebooks();
    } catch (err) {
      reportWhatsAppError(err, "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const handleExport = async () => {
    if (!selectedPhonebookId) return;
    setExporting(true);
    try {
      const res = await listPhonebookContacts(selectedPhonebookId, {
        search: searchQuery,
        limit: 10000,
        offset: 0,
      });
      const rows = unwrapWhatsAppList(res);
      if (!rows.length) {
        toast.error("No contacts to export");
        return;
      }
      exportContactsCsv(rows, selectedPhonebook?.name);
      toast.success("Contacts exported");
    } catch (err) {
      reportWhatsAppError(err, "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const allSelected =
    contacts.length > 0 && selectedIds.length === contacts.length;
  const pageStart =
    contactsTotal === 0 ? 0 : (contactPage - 1) * rowsPerPage + 1;
  const pageEnd = Math.min(contactPage * rowsPerPage, contactsTotal);

  return (
    <WhatsAppPageShell className="whatsapp-page-shell--panel">
      <div className="whatsapp-inbox-root">
        <div className="whatsapp-page-panel">
          <div className="wa-phonebook-page">
            <header className="wa-phonebook-page-header">
              <div className="wa-phonebook-page-title-row">
                <div className="wa-phonebook-page-icon">
                  <Icon icon="mdi:card-account-phone-outline" width={22} />
                </div>
                <div>
                  <h1>Phonebook</h1>
                  <p>Manage contacts and phonebooks</p>
                </div>
              </div>
            </header>

            <div className="wa-phonebook-layout">
              <aside className="wa-phonebook-list-panel">
                <div className="wa-phonebook-panel-head">
                  <div className="wa-phonebook-panel-head-left">
                    <div className="wa-phonebook-panel-head-icon">
                      <Icon icon="solar:notebook-bookmark-linear" width={18} />
                    </div>
                    <div>
                      <h2>Phonebooks</h2>
                      <span className="wa-phonebook-panel-sub">
                        {phonebooks.length} total
                      </span>
                    </div>
                  </div>
                  <span className="wa-phonebook-count-badge">
                    {phonebooks.length}
                  </span>
                </div>

                <form
                  className="wa-phonebook-create-form"
                  onSubmit={handleCreatePhonebook}
                >
                  <input
                    type="text"
                    placeholder="New phonebook name"
                    value={newPhonebookName}
                    onChange={(e) => setNewPhonebookName(e.target.value)}
                    aria-label="New phonebook name"
                  />
                  <button
                    type="submit"
                    className="wa-phonebook-add-btn"
                    disabled={creatingPhonebook || !newPhonebookName.trim()}
                  >
                    <Icon icon="solar:add-circle-linear" width={18} />
                    {creatingPhonebook ? "Adding..." : "Add Phonebook"}
                  </button>
                </form>

                <div className="wa-phonebook-list">
                  {phonebooksLoading ? (
                    <div className="whatsapp-list-empty">Loading...</div>
                  ) : null}
                  {!phonebooksLoading && phonebooksError ? (
                    <div className="whatsapp-list-error">{phonebooksError}</div>
                  ) : null}
                  {!phonebooksLoading &&
                  !phonebooksError &&
                  phonebooks.length === 0 ? (
                    <div className="whatsapp-list-empty">
                      No phonebooks yet. Create one above.
                    </div>
                  ) : null}
                  {!phonebooksLoading &&
                    phonebooks.map((pb) => {
                      const isActive = String(pb.id) === selectedPhonebookId;
                      return (
                        <div
                          key={pb.id}
                          className={`wa-phonebook-item ${isActive ? "active" : ""}`}
                        >
                          <button
                            type="button"
                            className="wa-phonebook-item-folder"
                            aria-hidden
                            tabIndex={-1}
                          >
                            <Icon icon="solar:folder-with-files-linear" width={20} />
                          </button>
                          <button
                            type="button"
                            className="wa-phonebook-item-main"
                            onClick={() => handleSelectPhonebook(pb.id)}
                          >
                            <span className="wa-phonebook-item-name">
                              {pb.name}
                            </span>
                            <span className="wa-phonebook-item-meta">
                              {formatCreatedAgo(pb.createdAt || pb.created_at)}
                            </span>
                          </button>
                          <div className="wa-phonebook-item-actions">
                            <button
                              type="button"
                              className="wa-phonebook-icon-btn wa-phonebook-icon-btn--add"
                              onClick={() => openAddContactModal(pb, "manual")}
                              aria-label={`Add contacts to ${pb.name}`}
                            >
                              <Icon icon="solar:add-circle-linear" width={18} />
                            </button>
                            <button
                              type="button"
                              className="wa-phonebook-icon-btn wa-phonebook-icon-btn--danger"
                              onClick={() => setDeletePhonebookTarget(pb)}
                              aria-label={`Delete ${pb.name}`}
                            >
                              <Icon
                                icon="solar:trash-bin-trash-linear"
                                width={16}
                              />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </aside>

              <section className="wa-phonebook-contacts-panel">
                <div className="wa-phonebook-panel-head">
                  <div className="wa-phonebook-panel-head-left">
                    <div className="wa-phonebook-panel-head-icon">
                      <Icon icon="solar:user-id-linear" width={18} />
                    </div>
                    <div>
                      <h2>Contacts</h2>
                    </div>
                    <span className="wa-phonebook-count-badge wa-phonebook-count-badge--sm">
                      {contactsTotal}
                    </span>
                  </div>
                  <div className="wa-phonebook-panel-actions">
                    <button
                      type="button"
                      className={`wa-phonebook-icon-btn wa-phonebook-icon-btn--toolbar ${selectedIds.length ? "wa-phonebook-icon-btn--danger" : ""}`}
                      onClick={() =>
                        selectedIds.length
                          ? setShowDeleteContactsConfirm(true)
                          : undefined
                      }
                      disabled={!selectedIds.length}
                      title={
                        selectedIds.length
                          ? `Delete ${selectedIds.length} selected`
                          : "Delete selected"
                      }
                    >
                      <Icon icon="solar:trash-bin-trash-linear" width={18} />
                    </button>
                    <button
                      type="button"
                      className="wa-phonebook-export-btn"
                      onClick={handleExport}
                      disabled={!selectedPhonebookId || exporting}
                    >
                      <Icon icon="solar:export-linear" width={16} />
                      {exporting ? "Exporting..." : "Export"}
                    </button>
                  </div>
                </div>

                {selectedPhonebook ? (
                  <>
                    <div className="wa-phonebook-contacts-search">
                      <div className="whatsapp-search-wrap whatsapp-search-wrap--inline">
                        <Icon
                          icon="solar:magnifer-line-duotone"
                          className="whatsapp-search-icon"
                          width={18}
                        />
                        <input
                          type="search"
                          placeholder="Search contacts..."
                          value={searchInput}
                          onChange={(e) => setSearchInput(e.target.value)}
                          aria-label="Search contacts"
                        />
                      </div>
                    </div>

                    <div className="wa-phonebook-contacts-body">
                      {contactsLoading ? (
                        <div className="whatsapp-list-empty">Loading contacts...</div>
                      ) : contactsError ? (
                        <div className="whatsapp-list-error">{contactsError}</div>
                      ) : contacts.length === 0 ? (
                        <div className="whatsapp-list-empty">
                          No contacts in this phonebook yet.
                        </div>
                      ) : (
                        <table className="wa-phonebook-contacts-table">
                          <thead>
                            <tr>
                              <th className="wa-phonebook-col-check">
                                <input
                                  type="checkbox"
                                  checked={allSelected}
                                  onChange={(e) =>
                                    toggleSelectAll(e.target.checked)
                                  }
                                  aria-label="Select all contacts"
                                />
                              </th>
                              <th>Name</th>
                              <th>Phonebook</th>
                              <th>Mobile number</th>
                              <th>Variable 1</th>
                              <th>Variable 2</th>
                              <th>Variable 3</th>
                              <th>Variable 4</th>
                              <th>Variable 5</th>
                              <th>Date</th>
                              <th aria-label="Actions" />
                            </tr>
                          </thead>
                          <tbody>
                            {contacts.map((contact) => (
                              <tr key={contact.id}>
                                <td className="wa-phonebook-col-check">
                                  <input
                                    type="checkbox"
                                    checked={selectedIds.includes(contact.id)}
                                    onChange={(e) =>
                                      toggleSelectContact(
                                        contact.id,
                                        e.target.checked,
                                      )
                                    }
                                    aria-label={`Select ${contact.name}`}
                                  />
                                </td>
                                <td>{contact.name || "—"}</td>
                                <td>
                                  <span className="wa-phonebook-chip wa-phonebook-chip--outline">
                                    {selectedPhonebook.name}
                                  </span>
                                </td>
                                <td>
                                  {formatWhatsAppMobile(contact.mobile) ||
                                    contact.mobile ||
                                    "—"}
                                </td>
                                <td>{contact.var1 || "—"}</td>
                                <td>{contact.var2 || "—"}</td>
                                <td>{contact.var3 || "—"}</td>
                                <td>{contact.var4 || "—"}</td>
                                <td>{contact.var5 || "—"}</td>
                                <td>
                                  {formatContactDate(
                                    contact.createdAt || contact.created_at,
                                  )}
                                </td>
                                <td>
                                  <button
                                    type="button"
                                    className="whatsapp-icon-btn"
                                    onClick={() => openEditContact(contact)}
                                    aria-label={`Edit ${contact.name}`}
                                  >
                                    <Icon
                                      icon="solar:pen-linear"
                                      width={16}
                                    />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>

                    <footer className="wa-phonebook-pagination">
                      <label>
                        Rows per page:{" "}
                        <select
                          value={rowsPerPage}
                          onChange={(e) =>
                            setRowsPerPage(Number(e.target.value))
                          }
                        >
                          {ROWS_PER_PAGE_OPTIONS.map((n) => (
                            <option key={n} value={n}>
                              {n}
                            </option>
                          ))}
                        </select>
                      </label>
                      <span>
                        {pageStart}-{pageEnd} of {contactsTotal}
                      </span>
                      <div className="wa-phonebook-pagination-nav">
                        <button
                          type="button"
                          disabled={contactPage <= 1}
                          onClick={() =>
                            setContactPage((p) => Math.max(1, p - 1))
                          }
                          aria-label="Previous page"
                        >
                          <Icon icon="solar:alt-arrow-left-linear" width={16} />
                        </button>
                        <button
                          type="button"
                          disabled={contactPage >= totalPages}
                          onClick={() =>
                            setContactPage((p) =>
                              Math.min(totalPages, p + 1),
                            )
                          }
                          aria-label="Next page"
                        >
                          <Icon icon="solar:alt-arrow-right-linear" width={16} />
                        </button>
                      </div>
                    </footer>
                  </>
                ) : (
                  <div className="whatsapp-empty-state">
                    <Icon
                      icon="mdi:card-account-phone-outline"
                      width={56}
                      style={{ opacity: 0.45 }}
                    />
                    <p>Select a phonebook to view contacts</p>
                  </div>
                )}
              </section>
            </div>
          </div>
        </div>
      </div>

      <Modal
        show={!!addContactModal}
        onHide={closeAddContactModal}
        centered
        size="lg"
        contentClassName={cardClass}
      >
        <Modal.Header closeButton>
          <Modal.Title id="add-contacts-title">
            {addContactModal
              ? `Add Contacts — ${addContactModal.phonebookName}`
              : "Add Contacts"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <div className="d-flex gap-2 mb-3">
            <button
              type="button"
              className={`btn flex-fill ${contactModalTab === "manual" ? "btn-primary" : "btn-light border"} ${buttonClass}`}
              onClick={() => setContactModalTab("manual")}
            >
              Manual Entry
            </button>
            <button
              type="button"
              className={`btn flex-fill ${contactModalTab === "csv" ? "btn-primary" : "btn-light border"} ${buttonClass}`}
              onClick={() => setContactModalTab("csv")}
            >
              Import CSV
            </button>
          </div>

          {contactFormError ? (
            <div className="alert alert-danger py-2">{contactFormError}</div>
          ) : null}

          {contactModalTab === "manual" ? (
            <form onSubmit={handleSaveContact}>
              <div className="row">
                <div className="mb-3 col-md-6">
                  <Form.Label>
                    Contact name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    className={buttonClass}
                    value={contactForm.name}
                    onChange={(e) =>
                      setContactForm((f) => ({ ...f, name: e.target.value }))
                    }
                    placeholder="Contact name"
                    required
                  />
                </div>
                <div className="mb-3 col-md-6">
                  <Form.Label>
                    Mobile number <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    {...mobileInputFieldProps}
                    className={buttonClass}
                    value={contactForm.mobile}
                    onChange={(e) =>
                      setContactForm((f) => ({
                        ...f,
                        mobile: sanitizeMobileInput(e.target.value),
                      }))
                    }
                    placeholder="Mobile number"
                    required
                  />
                </div>
              </div>

              <div className="rounded-3 border p-3 mb-3">
                <h6 className="fw-semibold mb-3">Additional fields</h6>
                <div className="row">
                  {["var1", "var2", "var3", "var4", "var5"].map((key, idx) => (
                    <div key={key} className="mb-3 col-md-6 col-lg-4">
                      <Form.Label>{`Variable ${idx + 1}`}</Form.Label>
                      <Form.Control
                        className={buttonClass}
                        value={contactForm[key]}
                        onChange={(e) =>
                          setContactForm((f) => ({
                            ...f,
                            [key]: e.target.value,
                          }))
                        }
                        placeholder={`Variable ${idx + 1}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="d-flex justify-content-end">
                <Button
                  type="submit"
                  variant="primary"
                  className={buttonClass}
                  disabled={savingContact}
                >
                  {savingContact ? "Saving..." : "Save Contact"}
                </Button>
              </div>
            </form>
          ) : (
            <>
              <button
                type="button"
                className={`wa-pb-sample-card border rounded-3 p-3 mb-3 w-100 ${buttonClass}`}
                onClick={downloadSampleCsv}
              >
                <Icon icon="solar:download-linear" width={22} />
                <div className="text-start">
                  <strong>Download Sample CSV</strong>
                  <small className="d-block text-muted">
                    Use this template to format your contacts
                  </small>
                </div>
              </button>

              <div className="rounded-3 border p-3">
                <h6 className="fw-semibold mb-3">Import file</h6>
                <div
                  className={`wa-pb-csv-dropzone ${importDragging ? "dragging" : ""}`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setImportDragging(true);
                  }}
                  onDragLeave={() => setImportDragging(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setImportDragging(false);
                    handleCsvFile(e.dataTransfer.files?.[0]);
                  }}
                  onClick={() => csvInputRef.current?.click()}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      csvInputRef.current?.click();
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <Icon icon="solar:cloud-upload-linear" width={36} />
                  <p className="mb-1">Click to select CSV file</p>
                  <small className="text-muted">Only .csv files are accepted</small>
                  {importFile ? (
                    <div className="mt-2 fw-semibold">{importFile.name}</div>
                  ) : null}
                  <input
                    ref={csvInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    className="d-none"
                    onChange={(e) => handleCsvFile(e.target.files?.[0])}
                  />
                </div>

                {importResult ? (
                  <div className="whatsapp-import-result mt-3">
                    <p className="mb-0">
                      <strong>Imported:</strong> {importResult.imported ?? 0}
                      {" · "}
                      <strong>Skipped:</strong> {importResult.skipped ?? 0}
                    </p>
                  </div>
                ) : null}

                <div className="d-flex justify-content-end mt-3">
                  <Button
                    type="button"
                    variant="primary"
                    className={buttonClass}
                    onClick={handleImportCsv}
                    disabled={!importFile || importing}
                  >
                    {importing ? "Importing..." : "Import CSV"}
                  </Button>
                </div>
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={!!editingContact}
        onHide={closeEditContact}
        centered
        size="lg"
        contentClassName={cardClass}
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Contact</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {contactFormError ? (
            <div className="alert alert-danger py-2">{contactFormError}</div>
          ) : null}
          <form onSubmit={handleSaveContact}>
            <div className="row">
              <div className="mb-3 col-md-6">
                <Form.Label>
                  Contact name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  className={buttonClass}
                  value={contactForm.name}
                  onChange={(e) =>
                    setContactForm((f) => ({ ...f, name: e.target.value }))
                  }
                  required
                />
              </div>
              <div className="mb-3 col-md-6">
                <Form.Label>
                  Mobile number <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  {...mobileInputFieldProps}
                  className={buttonClass}
                  value={contactForm.mobile}
                  onChange={(e) =>
                    setContactForm((f) => ({
                      ...f,
                      mobile: sanitizeMobileInput(e.target.value),
                    }))
                  }
                  required
                />
              </div>
            </div>
            <div className="rounded-3 border p-3 mb-3">
              <h6 className="fw-semibold mb-3">Additional fields</h6>
              <div className="row">
                {["var1", "var2", "var3", "var4", "var5"].map((key, idx) => (
                  <div key={key} className="mb-3 col-md-6 col-lg-4">
                    <Form.Label>{`Variable ${idx + 1}`}</Form.Label>
                    <Form.Control
                      className={buttonClass}
                      value={contactForm[key]}
                      onChange={(e) =>
                        setContactForm((f) => ({
                          ...f,
                          [key]: e.target.value,
                        }))
                      }
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className="d-flex justify-content-end">
              <Button
                type="submit"
                variant="primary"
                className={buttonClass}
                disabled={savingContact}
              >
                {savingContact ? "Saving..." : "Save Contact"}
              </Button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      <Modal
        show={!!deletePhonebookTarget}
        onHide={() => setDeletePhonebookTarget(null)}
        centered
        contentClassName={cardClass}
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete phonebook</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Delete <strong>{deletePhonebookTarget?.name}</strong> and all its
          contacts? This cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            className={buttonClass}
            onClick={() => setDeletePhonebookTarget(null)}
            disabled={deletingPhonebook}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            className={buttonClass}
            onClick={handleConfirmDeletePhonebook}
            disabled={deletingPhonebook}
          >
            {deletingPhonebook ? "Deleting..." : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDeleteContactsConfirm}
        onHide={() => setShowDeleteContactsConfirm(false)}
        centered
        contentClassName={cardClass}
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete contacts</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Delete {selectedIds.length} selected contact
          {selectedIds.length > 1 ? "s" : ""}?
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            className={buttonClass}
            onClick={() => setShowDeleteContactsConfirm(false)}
            disabled={deletingContacts}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            className={buttonClass}
            onClick={handleDeleteSelectedContacts}
            disabled={deletingContacts}
          >
            {deletingContacts ? "Deleting..." : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </WhatsAppPageShell>
  );
}
